import {
  FormSectionStateArray,
  FormState,
  PositionAndWeatherVm,
  PropellerVm,
  StateField,
  StateFieldType,
  WindVm,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { roundToPlaces } from '@navstation-portal/shared/util/math';
import { getFieldFromFormState, getFieldValueFromFormState, isNullOrNaN, isSectionValid } from '../vm-builder-helpers';

const WIND_FORCE_THRESHOLDS: number[] = [0.3, 1.5, 3.3, 5.5, 7.9, 10.7, 13.8, 17.1, 20.7, 24.4, 28.4, 32.6];

// The wind speeds are in m/s, and the GPS speed is in knots.
const METERS_PER_SECOND_PER_KNOT = 0.51444;

interface Wind {
  speed?: number;
  direction?: number;
}

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
const toDegrees = (radians: number) => (radians * 180) / Math.PI;
const clampToUnit = (value: number) => Math.min(1, Math.max(-1, value));
const roundOrUndefined = (value: number) => (isNaN(value) ? undefined : roundToPlaces(value, 2));
const isComplete = (wind: Wind): wind is Required<Wind> => !isNullOrNaN(wind.speed) && !isNullOrNaN(wind.direction);

// V1 copies the wind as it is while the vessel holds no GPS speed
// (tabs.directive.ts:1695 and :1727).
const hasNoShipSpeed = (observedSpeed: number) => isNullOrNaN(observedSpeed) || observedSpeed === 0;

// V1, tabs.directive.ts:1717.
const calculateTrueWind = (apparent: Wind, observedSpeed: number): Wind => {
  if (!isComplete(apparent)) return {};
  const { speed, direction } = apparent;
  if (hasNoShipSpeed(observedSpeed)) return { speed, direction };

  const shipSpeed = observedSpeed * METERS_PER_SECOND_PER_KNOT;
  const trueSpeed = Math.sqrt(speed ** 2 + shipSpeed ** 2 - 2 * speed * shipSpeed * Math.cos(toRadians(direction)));
  const fromBow = toDegrees(Math.acos(clampToUnit((speed * Math.cos(toRadians(direction)) - shipSpeed) / trueSpeed)));
  const trueDirection = direction > 180 ? 360 - fromBow : fromBow;

  return { speed: roundOrUndefined(trueSpeed), direction: roundOrUndefined(trueDirection) };
};

// V1, tabs.directive.ts:1684.
const calculateApparentWind = (trueWind: Wind, observedSpeed: number): Wind => {
  if (!isComplete(trueWind)) return {};
  const { speed, direction } = trueWind;
  if (hasNoShipSpeed(observedSpeed)) return { speed, direction };

  const shipSpeed = observedSpeed * METERS_PER_SECOND_PER_KNOT;
  const apparentSpeed = Math.sqrt(speed ** 2 + shipSpeed ** 2 + 2 * speed * shipSpeed * Math.cos(toRadians(direction)));
  const fromBow = toDegrees(
    Math.acos(clampToUnit((speed * Math.cos(toRadians(direction)) + shipSpeed) / apparentSpeed))
  );
  const apparentDirection = direction >= 180 ? 360 - fromBow : fromBow;

  return { speed: roundOrUndefined(apparentSpeed), direction: roundOrUndefined(apparentDirection) };
};

const OFFSET_FOR_CURRENT_TRANSFORM = 'scale(1,-1) translate(0,-54)';

const PITCH_POSITIVE_SIGN = '+';

const pitchSignPrefix = (value: number): string => (value > 0 ? PITCH_POSITIVE_SIGN : '');

const buildPropellerVm = (pitch: number | undefined): PropellerVm => ({
  pitch,
  pitchPrefix: pitchSignPrefix(pitch ?? 0),
});

const buildPropellersVm = (formState: FormState): PropellerVm[] => {
  const propellerPitches = getFieldFromFormState(formState, 'propellers', 'propellerPitches') as
    | FormSectionStateArray
    | undefined;

  return (propellerPitches ?? []).map((entry) =>
    buildPropellerVm((entry['pitch'] as StateField<StateFieldType>)?.value as number | undefined)
  );
};

export function buildPositionAndWeatherVm(formState: FormState): PositionAndWeatherVm {
  const isSpeedLogMalfunction = () => {
    const speedLogMalfunction = getFieldFromFormState(formState, 'speedAndDistance', 'speedLogMalfunction');

    return Boolean((speedLogMalfunction as StateField<StateFieldType>)?.value);
  };

  const isTrueWindReporting = () => {
    const trueWindReporting = getFieldFromFormState(formState, 'wind', 'trueWindReporting');

    return Boolean((trueWindReporting as StateField<StateFieldType>)?.value);
  };

  // True Wind Reporting picks the pair the user types. The other pair is the
  // calculated one. The section shows each value, and its control writes it to
  // the form state.
  const buildWindVm = (): WindVm => {
    const windValue = (fieldId: string) => getFieldValueFromFormState(formState, 'wind', fieldId) as number;
    const apparent = { speed: windValue('apparentWindSpeed'), direction: windValue('apparentWindDirection') };
    const trueWind = { speed: windValue('trueWindSpeed'), direction: windValue('trueWindDirection') };
    const observedSpeed = getFieldValueFromFormState(formState, 'speedAndDistance', 'observedSpeed') as number;

    if (isTrueWindReporting()) {
      const calculated = calculateApparentWind(trueWind, observedSpeed);
      return {
        apparentWindSpeed: calculated.speed,
        apparentWindDirection: calculated.direction,
        trueWindSpeed: trueWind.speed,
        trueWindDirection: trueWind.direction,
      };
    }
    const calculated = calculateTrueWind(apparent, observedSpeed);
    return {
      apparentWindSpeed: apparent.speed,
      apparentWindDirection: apparent.direction,
      trueWindSpeed: calculated.speed,
      trueWindDirection: calculated.direction,
    };
  };
  const wind = buildWindVm();

  const calculateTrueWindDirection = () => {
    const trueWindDirection = wind.trueWindDirection as number;
    const courseOverGroundField = getFieldFromFormState(formState, 'speedAndDistance', 'courseOverGround');
    const courseOverGround = (courseOverGroundField as StateField<StateFieldType>)?.value as number;
    if (isNullOrNaN(trueWindDirection) || isNullOrNaN(courseOverGround)) return;

    const trueWindEffect = roundToPlaces((trueWindDirection + courseOverGround) % 360, 2);
    if (isNaN(trueWindEffect)) return;

    return trueWindEffect;
  };

  const calculateTrueWindForce = () => {
    const trueWindSpeed = wind.trueWindSpeed as number;
    if (isNullOrNaN(trueWindSpeed)) return;

    let trueWindForce = 0;
    for (const threshold of WIND_FORCE_THRESHOLDS) {
      if (trueWindSpeed < threshold) {
        break;
      }
      trueWindForce++;
    }

    return trueWindForce;
  };

  const calculateApparentCurrent = () => {
    const failedResult = {
      apparentCurrentSpeed: undefined,
      apparentCurrentDirection: undefined,
    };

    const trueCurrentSpeed = getFieldValueFromFormState(formState, 'currentsAndSeaWater', 'trueCurrentSpeed') as number;
    if (isNullOrNaN(trueCurrentSpeed)) return failedResult;

    const currentDirectionTrueNorth = getFieldValueFromFormState(
      formState,
      'currentsAndSeaWater',
      'currentDirectionTrueNorth'
    ) as number;
    if (isNullOrNaN(currentDirectionTrueNorth)) return failedResult;

    // V1 copies the true current while the vessel holds no GPS speed, or no
    // course over ground (tabs.directive.ts:1770).
    const observedSpeed = getFieldValueFromFormState(formState, 'speedAndDistance', 'observedSpeed') as number;
    const courseOverGround = getFieldValueFromFormState(formState, 'speedAndDistance', 'courseOverGround') as number;
    if (hasNoShipSpeed(observedSpeed) || isNullOrNaN(courseOverGround)) {
      return {
        apparentCurrentSpeed: roundToPlaces(trueCurrentSpeed, 2),
        apparentCurrentDirection: roundToPlaces(currentDirectionTrueNorth, 2),
      };
    }

    let apparentCurrentDirection = currentDirectionTrueNorth - courseOverGround;
    if (apparentCurrentDirection < 0) apparentCurrentDirection += 360;
    const apparentCurrentSpeed = trueCurrentSpeed * Math.cos((apparentCurrentDirection * Math.PI) / 180);

    return {
      apparentCurrentSpeed: roundToPlaces(apparentCurrentSpeed, 2),
      apparentCurrentDirection: roundToPlaces(apparentCurrentDirection, 2),
    };
  };

  const apparentWaveDirection =
    (getFieldValueFromFormState(formState, 'currentsAndSeaWater', 'apparentWaveDirection') as number) ?? undefined;

  const apparentWindDirection =
    (getFieldValueFromFormState(formState, 'wind', 'apparentWindDirection') as number) ?? undefined;

  const getDirection = (value = 0, isCurrentDir: boolean) => {
    if (!isCurrentDir) value += 180;
    const centerX = 280;
    const centerY = 180;
    const height = 130;
    const shiftDown = centerY + height * Math.cos((value * Math.PI) / 180);
    const shiftLeft = centerX - (height * Math.cos(((90 - value) * Math.PI) / 180)) / 1.5;

    const baseTransform = `translate(${shiftLeft}, ${shiftDown}) rotate(${value})`;
    return isCurrentDir ? `${baseTransform} ${OFFSET_FOR_CURRENT_TRANSFORM}` : baseTransform;
  };

  const isObservationValid = isSectionValid(formState['observation']);
  const isWindValid = isSectionValid(formState['wind']);
  const isSpeedAndDistanceValid = isSectionValid(formState['speedAndDistance']);
  const isShipPositionValid = isSectionValid(formState['shipPosition']);
  const isPositionAndWeatherValid = isObservationValid && isWindValid && isSpeedAndDistanceValid && isShipPositionValid;

  const { apparentCurrentSpeed, apparentCurrentDirection } = calculateApparentCurrent();
  const isWindArrowVisible = apparentWindDirection != undefined ? true : false;
  const isWaveArrowVisible = apparentWaveDirection != undefined ? true : false;
  const isCurArrowVisible = apparentCurrentDirection != undefined ? true : false;
  const propellers = buildPropellersVm(formState);

  return {
    wind,
    isTrueWindReporting: isTrueWindReporting(),
    isSpeedLogMalfunction: isSpeedLogMalfunction(),
    trueWindForce: calculateTrueWindForce(),
    trueWindDirection: calculateTrueWindDirection(),
    apparentCurrentDirection,
    apparentCurrentSpeed,
    apparentWaveDirection,
    apparentWindDirection,
    windDirectionTransform: getDirection(apparentWindDirection, false),
    waveDirectionTransform: getDirection(apparentWaveDirection, false),
    currentDirectionTransform: getDirection(apparentCurrentDirection, true),
    isWindArrowVisible,
    isCurArrowVisible,
    isWaveArrowVisible,
    isObservationValid,
    isWindValid,
    isSpeedAndDistanceValid,
    isPositionAndWeatherValid,
    hasMultiplePropellers: propellers.length > 1,
    propellers,
  };
}
